/**
 * Manual Entry Screen (Web)
 * Allows users to manually add a shooting session by uploading/capturing target photo
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MDBContainer,
  MDBCard,
  MDBCardBody,
  MDBBtn,
  MDBIcon,
  MDBTypography,
  MDBInput,
  MDBTable,
  MDBTableHead,
  MDBTableBody
} from 'mdb-react-ui-kit';
import { ShotData, ShootingSession, InventoryItem, SessionLocation } from '../types';
import { getApiUrl } from '../config/api';
import { authService } from '../services/authService';
import { inventoryService } from '../services/inventoryService';
import { settingsService } from '../services/settingsService';
import './ManualEntry.css';

interface ManualEntryProps {
  theme: 'light' | 'dark';
}

type EntryStep = 'choice' | 'camera' | 'reviewing' | 'editing';

interface DetectedShot extends ShotData {
  id: string;
}

const ManualEntry: React.FC<ManualEntryProps> = ({ theme }) => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  
  const [currentStep, setCurrentStep] = useState<EntryStep>('choice');
  const [photoDataUrl, setPhotoDataUrl] = useState<string | null>(null);
  const [outputVisualization, setOutputVisualization] = useState<string | null>(null);
  const [displayedImage, setDisplayedImage] = useState<string | null>(null);
  const [sessionDate] = useState<number>(Date.now());
  const [detectedShots, setDetectedShots] = useState<DetectedShot[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Score state - can be manually overridden by user
  const [manualTotalScore, setManualTotalScore] = useState<string | null>(null);
  
  // Track if AI analysis was performed and detected shots
  const [aiAnalyzed, setAiAnalyzed] = useState(false);
  
  // Inventory state
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [selectedInventoryItemId, setSelectedInventoryItemId] = useState<string>('');
  
  // Location state
  const [useLocation, setUseLocation] = useState(false);
  const [sessionLocation, setSessionLocation] = useState<SessionLocation | null>(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  
  // Session notes
  const [sessionNotes, setSessionNotes] = useState<string>('');

  /**
   * Calculate total score from detected shots (sum of ring values)
   * This matches the calculation logic in the SolidRange mobile app
   */
  const calculateTotalScore = (shots: DetectedShot[]): number => {
    return shots.reduce((sum, shot) => sum + shot.ring, 0);
  };

  // Calculated value from shots (matches mobile app logic)
  const calculatedTotalScore = calculateTotalScore(detectedShots);
  
  // Use manual override if set, otherwise use calculated value
  const totalScore = manualTotalScore !== null ? parseFloat(manualTotalScore) || 0 : calculatedTotalScore;
  const averageScore = detectedShots.length > 0 ? totalScore / detectedShots.length : 0;

  // Reset manual override when shots change (user can still override again)
  useEffect(() => {
    setManualTotalScore(null);
  }, [detectedShots.length]);

  // Load location tracking preference from settings
  useEffect(() => {
    const loadLocationSetting = async () => {
      const locationEnabled = await settingsService.isLocationEnabled();
      setUseLocation(locationEnabled);
      
      // If location is enabled in settings, capture it immediately
      if (locationEnabled) {
        captureLocation();
      }
    };
    
    loadLocationSetting();
  }, []);

  /**
   * Capture current location using browser Geolocation API
   */
  const captureLocation = async () => {
    if (!navigator.geolocation) {
      console.warn('[ManualEntry] Geolocation not supported by browser');
      return;
    }

    setIsLoadingLocation(true);
    
    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000, // Cache for 1 minute
        });
      });

      const location: SessionLocation = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        timestamp: Date.now(),
      };

      // Try to get address using reverse geocoding (OpenStreetMap Nominatim)
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${location.latitude}&lon=${location.longitude}&zoom=16`,
          { headers: { 'Accept-Language': 'en' } }
        );
        if (response.ok) {
          const data = await response.json();
          location.address = data.display_name || undefined;
        }
      } catch (geocodeError) {
        console.warn('[ManualEntry] Failed to get address:', geocodeError);
      }

      setSessionLocation(location);
      console.log('[ManualEntry] Location captured:', location.address || `${location.latitude}, ${location.longitude}`);
    } catch (error: any) {
      console.error('[ManualEntry] Failed to get location:', error);
      if (error.code === 1) {
        alert('Location permission denied. Please enable location access in your browser settings.');
      } else if (error.code === 2) {
        alert('Unable to determine your location. Please check your device settings.');
      } else if (error.code === 3) {
        alert('Location request timed out. Please try again.');
      }
      setUseLocation(false);
    } finally {
      setIsLoadingLocation(false);
    }
  };

  /**
   * Handle location toggle
   */
  const handleLocationToggle = async () => {
    const newValue = !useLocation;
    setUseLocation(newValue);
    
    if (newValue) {
      await captureLocation();
    } else {
      setSessionLocation(null);
    }
  };

  // Load inventory items on mount
  useEffect(() => {
    const loadInventory = async () => {
      try {
        const items = await inventoryService.getInventory();
        setInventoryItems(items);
        
        // Set default item if available
        const defaultItem = items.find(item => item.isDefault);
        if (defaultItem) {
          setSelectedInventoryItemId(defaultItem.id);
        }
      } catch (error) {
        console.error('[ManualEntry] Failed to load inventory:', error);
      }
    };
    loadInventory();
  }, []);

  const handleTakePhoto = async () => {
    try {
      setCurrentStep('camera');
      setIsProcessing(true);

      // Request camera access
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          width: { ideal: 1280 }, 
          height: { ideal: 720 },
          facingMode: 'environment'
        } 
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        await videoRef.current.play();
      }
      
      setIsProcessing(false);
    } catch (error) {
      alert('Failed to access camera. Please check permissions.');
      setCurrentStep('choice');
      setIsProcessing(false);
    }
  };

  const handleCapturePhoto = async () => {
    if (!videoRef.current) return;

    try {
      setIsProcessing(true);
      
      // Capture photo from video
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      
      ctx.drawImage(videoRef.current, 0, 0);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
      
      // Stop camera
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      
      // Compress and analyze photo
      const compressedDataUrl = await compressImage(dataUrl);
      await analyzePhoto(compressedDataUrl);
    } catch (error) {
      alert('Failed to capture photo. Please try again.');
      setIsProcessing(false);
    }
  };

  const handleUploadPhoto = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsProcessing(true);
      
      // Convert file to data URL
      const reader = new FileReader();
      reader.onload = async (e) => {
        const dataUrl = e.target?.result as string;
        // Compress before analyzing
        const compressedDataUrl = await compressImage(dataUrl);
        await analyzePhoto(compressedDataUrl);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      alert('Failed to process photo. Please try again.');
      setIsProcessing(false);
    }
  };

  const compressImage = async (dataUrl: string): Promise<string> => {
    try {
      const originalSize = Math.round(dataUrl.length / 1024);

      // Create an image element
      const img = new Image();
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = dataUrl;
      });

      // Create a canvas to resize and compress
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return dataUrl;
      }

      // Calculate new dimensions (max 800px width for smaller document size)
      const maxWidth = 800;
      let width = img.width;
      let height = img.height;

      if (width > maxWidth) {
        height = Math.round((height * maxWidth) / width);
        width = maxWidth;
      }

      canvas.width = width;
      canvas.height = height;

      // Draw and compress
      ctx.drawImage(img, 0, 0, width, height);
      const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.6);
      
      return compressedDataUrl;
    } catch (error) {
      return dataUrl;
    }
  };

  const analyzePhoto = async (dataUrl: string) => {
    try {
      setCurrentStep('reviewing');
      setPhotoDataUrl(dataUrl);
      
      // Send base64 image to API using configured endpoint
      const detectionUrl = `${getApiUrl('detection')}`;
      
      // Use makeAuthenticatedRequest for X-ZUMO-AUTH header (required for Azure Easy Auth)
      const response = await authService.makeAuthenticatedRequest(detectionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          imageBase64: dataUrl,
          confidence: 0.25,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        
        // If unauthorized, user needs to re-authenticate
        if (response.status === 401) {
          alert('Your session has expired. Please sign in again.');
          await authService.signOut();
          throw new Error('Authentication expired');
        }
        
        throw new Error(`API request failed: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Extract outputVisualization if present
      if (data.analysis?.outputVisualization) {
        const base64Visualization = `data:image/jpeg;base64,${data.analysis.outputVisualization}`;
        setOutputVisualization(base64Visualization);
        setDisplayedImage(base64Visualization);
      } else {
        // No visualization, use original photo
        setOutputVisualization(null);
        setDisplayedImage(dataUrl);
      }
      
      // Process all detections from the API
      const shots: DetectedShot[] = [];
      
      if (data.success && data.analysis.detections && data.analysis.detections.length > 0) {
        const imageWidth = data.analysis.imageWidth || 1920;
        const imageHeight = data.analysis.imageHeight || 1920;
        const centerX = imageWidth / 2;
        const centerY = imageHeight / 2;
        const targetRadius = Math.min(imageWidth, imageHeight) * 0.3;
        
        // Filter and convert detections to shots (only Bullet_ classes)
        const bulletDetections = data.analysis.detections.filter((d: any) => 
          d.class && d.class.startsWith('Bullet_')
        );
        
        bulletDetections.forEach((detection: any, index: number) => {
          // Calculate normalized coordinates
          const offsetX = detection.bbox.x - centerX;
          const offsetY = detection.bbox.y - centerY;
          const normalizedX = Math.round((offsetX / targetRadius) * 100);
          const normalizedY = Math.round((offsetY / targetRadius) * 100);
          const x = Math.max(-100, Math.min(100, normalizedX));
          const y = Math.max(-100, Math.min(100, normalizedY));
          
          // Use ring from API
          const ring = detection.ring || 0;
          
          shots.push({
            id: `shot_${Date.now()}_${index}`,
            shot: index + 1,
            ring,
            x,
            y,
            timestamp: Date.now(),
          });
        });
      }
      
      setDetectedShots(shots);
      setCurrentStep('editing');
      setIsProcessing(false);
      
      // Mark as AI analyzed if shots were detected
      if (shots.length > 0) {
        setAiAnalyzed(true);
      } else {
        setAiAnalyzed(false);
        alert('No bullet holes detected. You can add shots manually using the "+ Add Shot" button.');
      }
    } catch (error) {
      alert('Failed to analyze photo. You can still add shots manually.');
      setDetectedShots([]);
      setAiAnalyzed(false);
      setCurrentStep('editing');
      setIsProcessing(false);
    }
  };

  const handleRingChange = (id: string, newRing: string) => {
    const ring = parseInt(newRing, 10);
    if (isNaN(ring) || ring < 0 || ring > 10) return;

    setDetectedShots(prev =>
      prev.map(shot =>
        shot.id === id
          ? { ...shot, ring }
          : shot
      )
    );
  };

  const handleDeleteShot = (id: string) => {
    setDetectedShots(prev => {
      const filtered = prev.filter(shot => shot.id !== id);
      // Renumber shots
      return filtered.map((shot, index) => ({ ...shot, shot: index + 1 }));
    });
  };

  const handleAddShot = () => {
    const newShot: DetectedShot = {
      id: `shot_${Date.now()}_${detectedShots.length}`,
      shot: detectedShots.length + 1,
      ring: 0,
      x: 0,
      y: 0,
      timestamp: Date.now(),
    };
    setDetectedShots(prev => [...prev, newShot]);
  };

  const handleSaveSession = async () => {
    try {
      setIsProcessing(true);

      // Get user ID from auth service
      const user = authService.getUser();
      if (!user || !user.id) {
        alert('Please log in to save sessions');
        setIsProcessing(false);
        return;
      }
      const userId = user.id;

      // Generate session ID FIRST before uploading images
      const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Prepare session data
      // Use displayedImage (which could be outputVisualization or original photo)
      const imageToUpload = displayedImage || photoDataUrl;

      const session: ShootingSession = {
        id: sessionId,
        date: sessionDate,
        shots: detectedShots.map(({ id, ...shot }) => shot),
        totalScore: totalScore, // Auto-calculated from shots (sum of ring values)
        shotCount: detectedShots.length,
        averageScore: averageScore, // Auto-calculated (totalScore / shotCount)
        duration: 0,
        sessionType: aiAnalyzed ? 'automatic' : 'manual',
        targetImageData: imageToUpload || undefined, // Send base64 directly
        location: useLocation && sessionLocation ? sessionLocation : undefined,
        notes: sessionNotes.trim() || undefined,
        lastModified: Date.now(),
        syncVersion: 1,
        inventoryItemId: selectedInventoryItemId || undefined,
      };

      // Save to server via sync endpoint using makeAuthenticatedRequest for X-ZUMO-AUTH header
      const response = await authService.makeAuthenticatedRequest(`${getApiUrl('sync')}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          deviceId: 'web-browser',
          sessions: [session],
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        
        // If unauthorized, user needs to re-authenticate
        if (response.status === 401) {
          alert('Your session has expired. Please sign in again.');
          await authService.signOut();
          return;
        }
        
        throw new Error(`Save failed: ${response.status} ${response.statusText}`);
      }
      
      navigate('/sessions');
    } catch (error) {
      alert('Failed to save session. Please try again.');
      setIsProcessing(false);
    }
  };

  const handleCancel = () => {
    // Stop camera if active
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    
    if (detectedShots.length > 0) {
      if (window.confirm('You have unsaved shots. Are you sure you want to cancel?')) {
        navigate('/');
      }
    } else {
      navigate('/');
    }
  };

  const formatDate = (timestamp: number): string => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Step 1: Choice screen
  if (currentStep === 'choice') {
    return (
      <MDBContainer className="py-4">
        <MDBTypography tag="h1" className="text-center mb-2">Add Target Image</MDBTypography>
        <MDBTypography className="text-center text-muted mb-5">
          Choose how to add your shooting target
        </MDBTypography>
        
        <div className="row g-4 mb-4">
          <div className="col-md-6">
            <MDBCard className="h-100 choice-card" onClick={handleTakePhoto} style={{ cursor: 'pointer' }}>
              <MDBCardBody className="text-center py-5">
                <MDBIcon fas icon="camera" size="4x" className="text-primary mb-3" />
                <MDBTypography tag="h4" className="mb-2">Take Photo</MDBTypography>
                <MDBTypography className="text-muted">
                  Use your camera to capture the target
                </MDBTypography>
              </MDBCardBody>
            </MDBCard>
          </div>
          
          <div className="col-md-6">
            <MDBCard className="h-100 choice-card" onClick={handleUploadPhoto} style={{ cursor: 'pointer' }}>
              <MDBCardBody className="text-center py-5">
                <MDBIcon fas icon="file-image" size="4x" className="text-primary mb-3" />
                <MDBTypography tag="h4" className="mb-2">Upload Photo</MDBTypography>
                <MDBTypography className="text-muted">
                  Choose from your files
                </MDBTypography>
              </MDBCardBody>
            </MDBCard>
          </div>
        </div>
        
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileSelected}
        />
        
        <div className="text-center">
          <MDBBtn color="light" onClick={handleCancel}>
            <MDBIcon fas icon="arrow-left" className="me-2" />
            Back to Range
          </MDBBtn>
        </div>
      </MDBContainer>
    );
  }

  // Step 2: Camera view
  if (currentStep === 'camera') {
    return (
      <div className="manual-entry-container">
        <div className="camera-view">
          <video ref={videoRef} className="camera-video" autoPlay playsInline muted />
          <div className="camera-overlay">
            <h2>Position target in frame</h2>
            <p>Take a clear photo of your shooting target</p>
          </div>
          <div className="camera-controls">
            <button 
              className="btn btn-secondary" 
              onClick={() => {
                if (streamRef.current) {
                  streamRef.current.getTracks().forEach(track => track.stop());
                }
                setCurrentStep('choice');
              }}
            >
              Back
            </button>
            <button
              className="btn btn-capture"
              onClick={handleCapturePhoto}
              disabled={isProcessing}
            >
              <div className="capture-circle"></div>
            </button>
            <div className="spacer"></div>
          </div>
        </div>
      </div>
    );
  }

  // Step 3: Reviewing/analyzing
  if (currentStep === 'reviewing') {
    return (
      <MDBContainer className="py-4">
        <MDBCard>
          <MDBCardBody className="text-center py-5">
            <MDBIcon fas icon="search" spin size="3x" className="text-primary mb-3" />
            <MDBTypography tag="h4" className="mb-2">Analyzing target image...</MDBTypography>
            <MDBTypography className="text-muted">
              Detecting shot holes and calculating scores
            </MDBTypography>
          </MDBCardBody>
        </MDBCard>
      </MDBContainer>
    );
  }

  // Step 4: Editing
  return (
    <MDBContainer className="py-4">
      <MDBTypography tag="h1" className="text-center mb-4">Manual Session Entry</MDBTypography>
      
      <MDBCard className="mb-4">
        <MDBCardBody>
          <MDBTypography className="text-muted small mb-1">Session Date</MDBTypography>
          <MDBTypography tag="h6">{formatDate(sessionDate)}</MDBTypography>
        </MDBCardBody>
      </MDBCard>

      {displayedImage && (
        <MDBCard className="mb-4">
          <MDBCardBody>
            <MDBTypography tag="h5" className="mb-3">
              <MDBIcon fas icon="image" className="me-2" />
              {outputVisualization ? 'AI-Annotated Target Image' : 'Target Photo'}
            </MDBTypography>
            <div className="text-center">
              <img 
                src={displayedImage} 
                alt="Target" 
                className="img-fluid rounded"
                style={{ maxWidth: '100%', maxHeight: '500px' }}
              />
            </div>
          </MDBCardBody>
        </MDBCard>
      )}

      <MDBCard className="mb-4">
        <MDBCardBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <MDBTypography tag="h5" className="mb-0">
              <MDBIcon fas icon="bullseye" className="me-2" />
              Detected Shots ({detectedShots.length})
            </MDBTypography>
            <MDBBtn color="success" size="sm" onClick={handleAddShot}>
              <MDBIcon fas icon="plus" className="me-2" />
              Add Shot
            </MDBBtn>
          </div>

          {detectedShots.length === 0 ? (
            <div className="text-center py-4">
              <MDBIcon fas icon="bullseye" size="3x" className="text-muted mb-3" />
              <MDBTypography className="text-muted">No shots detected</MDBTypography>
              <MDBTypography className="text-muted small">
                Tap "+ Add Shot" to manually add shots
              </MDBTypography>
            </div>
          ) : (
            <div className="table-responsive">
              <MDBTable striped hover>
                <MDBTableHead>
                  <tr>
                    <th>Shot</th>
                    <th>Ring</th>
                    <th>Action</th>
                  </tr>
                </MDBTableHead>
                <MDBTableBody>
                  {detectedShots.map((shot) => (
                    <tr key={shot.id}>
                      <td>{shot.shot}</td>
                      <td>
                        <MDBInput
                          type="number"
                          value={shot.ring}
                          onChange={(e) => handleRingChange(shot.id, e.target.value)}
                          min="0"
                          max="10"
                          step="1"
                          size="sm"
                          style={{ width: '100px' }}
                        />
                      </td>
                      <td>
                        <MDBBtn
                          color="danger"
                          size="sm"
                          floating
                          onClick={() => handleDeleteShot(shot.id)}
                          title="Delete shot"
                        >
                          <MDBIcon fas icon="trash" />
                        </MDBBtn>
                      </td>
                    </tr>
                  ))}
                </MDBTableBody>
              </MDBTable>
            </div>
          )}
        </MDBCardBody>
      </MDBCard>

      {/* Weapon Selection Card */}
      <MDBCard className="mb-4">
        <MDBCardBody>
          <MDBTypography tag="h5" className="mb-3">
            <MDBIcon fas icon="crosshairs" className="me-2" />
            Weapon
          </MDBTypography>
          <select
            className="form-select"
            value={selectedInventoryItemId}
            onChange={(e) => setSelectedInventoryItemId(e.target.value)}
          >
            <option value="">Select weapon (optional)</option>
            {inventoryItems.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name} {item.caliber ? `(${item.caliber})` : ''}
              </option>
            ))}
          </select>
          {inventoryItems.length === 0 && (
            <MDBTypography className="text-muted small mt-2">
              No weapons in inventory. Add weapons in Settings → Inventory.
            </MDBTypography>
          )}
        </MDBCardBody>
      </MDBCard>

      {/* Location Card */}
      <MDBCard className="mb-4">
        <MDBCardBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <MDBTypography tag="h5" className="mb-0">
              <MDBIcon fas icon="map-marker-alt" className="me-2" />
              Location
            </MDBTypography>
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="locationToggle"
                checked={useLocation}
                onChange={handleLocationToggle}
                disabled={isLoadingLocation}
              />
              <label className="form-check-label" htmlFor="locationToggle">
                {isLoadingLocation ? 'Getting location...' : 'Save location'}
              </label>
            </div>
          </div>
          {useLocation && sessionLocation && (
            <div className="mt-2">
              <MDBTypography className="text-muted small mb-1">
                <MDBIcon fas icon="map-pin" className="me-2" />
                {sessionLocation.address || `${sessionLocation.latitude.toFixed(6)}, ${sessionLocation.longitude.toFixed(6)}`}
              </MDBTypography>
            </div>
          )}
          {useLocation && !sessionLocation && !isLoadingLocation && (
            <div className="mt-2">
              <MDBBtn size="sm" color="secondary" onClick={captureLocation}>
                <MDBIcon fas icon="sync" className="me-2" />
                Retry Location
              </MDBBtn>
            </div>
          )}
        </MDBCardBody>
      </MDBCard>

      {/* Notes Card */}
      <MDBCard className="mb-4">
        <MDBCardBody>
          <MDBTypography tag="h5" className="mb-3">
            <MDBIcon fas icon="sticky-note" className="me-2" />
            Notes
          </MDBTypography>
          <textarea
            className="form-control"
            rows={3}
            placeholder="Add notes about this session (optional)"
            value={sessionNotes}
            onChange={(e) => setSessionNotes(e.target.value)}
          />
        </MDBCardBody>
      </MDBCard>

      <MDBCard className="mb-4">
        <MDBCardBody>
          <MDBTypography tag="h5" className="mb-3">
            <MDBIcon fas icon="chart-line" className="me-2" />
            Session Summary
          </MDBTypography>
          <div className="d-flex justify-content-between align-items-center mb-3 pb-3 border-bottom">
            <MDBTypography>Total Shots:</MDBTypography>
            <MDBTypography tag="h5" className="mb-0 text-primary">{detectedShots.length}</MDBTypography>
          </div>
          <div className="d-flex justify-content-between align-items-center mb-3 pb-3 border-bottom">
            <div>
              <MDBTypography className="mb-0">Total Score:</MDBTypography>
              <MDBTypography className="text-muted small mb-0">
                Calculated: {calculatedTotalScore}
              </MDBTypography>
            </div>
            <MDBInput
              type="number"
              value={manualTotalScore !== null ? manualTotalScore : calculatedTotalScore.toString()}
              onChange={(e) => setManualTotalScore(e.target.value)}
              onBlur={() => {
                // If user clears the field or enters invalid value, reset to calculated
                if (manualTotalScore === '' || manualTotalScore === null) {
                  setManualTotalScore(null);
                }
              }}
              step="0.1"
              min="0"
              style={{ width: '120px', textAlign: 'right' }}
            />
          </div>
          <div className="d-flex justify-content-between align-items-center">
            <MDBTypography>Average Score:</MDBTypography>
            <MDBTypography tag="h5" className="mb-0 text-primary">{averageScore.toFixed(1)}</MDBTypography>
          </div>
        </MDBCardBody>
      </MDBCard>

      <div className="d-flex justify-content-end gap-2">
        <MDBBtn
          color="secondary"
          onClick={handleCancel}
          disabled={isProcessing}
        >
          Cancel
        </MDBBtn>
        <MDBBtn
          color="primary"
          onClick={handleSaveSession}
          disabled={isProcessing || detectedShots.length === 0}
        >
          {isProcessing ? (
            <>
              <MDBIcon fas icon="spinner" spin className="me-2" />
              Saving...
            </>
          ) : (
            <>
              <MDBIcon fas icon="save" className="me-2" />
              Save Session
            </>
          )}
        </MDBBtn>
      </div>
    </MDBContainer>
  );
};

export default ManualEntry;
